import React, { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import FocusTrap from 'focus-trap-react';
import { Headline, IconButton } from '@oneaudi/unified-web-alpha-components';
import CancelIcon from '../assets/icons/CancelIcon';
import {
  DigitalLeadsWrapper,
  DigitalLeadsModal,
  CloseModal,
  Content,
  Header,
  InputSection,
  VehicleDetailSection,
  BlackBackground,
  Sticky,
} from './DitigalLeads.styles';
import VehicleInfo from './VehicleInfo/VehicleInfo';
import Submit from './Submit';
import useDealerInfo from '../hooks/useDealerInfo';
import useVehicleInfo from '../hooks/useVehicleInfo';
import useLeadsBuilder from '../hooks/useLeadsBuilder';
import useLockBodyScroll from '../hooks/useLockBodyScroll';
import LegalDisclaimer from './LegalDisclaimer';
import ResultModal from './ResultModal';
import { selectLocalConfig, setModalIsClose } from '../redux/localConfigReducer';
import { selectForm } from '../redux/formReducer';
import { removeVehicleInfo, selectDealerAndVehicleData } from '../redux/dealerAndVehicleReducer';
import useSourceOfLead from '../hooks/useSourceOfLead';
import {
  useGenericFormLoad,
  useSuccessfulFormSubmission,
  useUnsuccessfulFormError,
  useIxisFormTracking,
} from '../hooks/useAnalyticsEventsIxis';
import { convertToPromotionsType } from '../functions/convertToPromotionsType';
import {
  selectAppConfig,
  setClearVehicleData,
  setRemoveOffersAndFYM,
} from '../redux/appConfigReducer';
import type { StockCarTypes } from '../types';
import getDataAttributes from '../functions/getDataAttributes';
import { IS_T1_PAGE } from '../constants/environment';
import { cleanInputs, selectInputs } from '../redux/inputsReducer';
import { INPUT_TYPE } from '../constants';
import {
  setModelLineSelectCustom,
  setModelLinesSelected,
  setHandraiserConfig,
} from '../utils/analyticsEventListeners';
import useGetFormTitle from '../hooks/useGetFormTitle';

// ============================================================================
// COMPONENT CONFIGURATION
// ============================================================================

const isLegacy = process.env.IS_LEGACY;

// ============================================================================
// HELPER: Form Name Resolver
// ============================================================================

function getFormName(isGenericForm: boolean, leadSource?: string): string {
  if (isGenericForm) return 'Generic Lead';
  if (leadSource === 'special_offers' || leadSource === 'offers') return 'Promotions Lead';
  return 'VTP Lead';
}

// ============================================================================
// SUBCOMPONENT: Modal Header
// ============================================================================

interface ModalHeaderProps {
  dealerName?: string;
  formTitle?: string | null;
  onClose: () => void;
}

const ModalHeader: React.FC<ModalHeaderProps> = ({ dealerName, formTitle, onClose }) => {
  const { t } = useTranslation();

  const headerText =
    formTitle ??
    (dealerName
      ? t('digitalLeads.contactDealer', { dealerName })
      : t('digitalLeads.contactDealerInYourArea'));

  return (
    <>
      <Sticky>
        {isLegacy ? (
          <CloseModal onClick={onClose} aria-label="close" data-testid="close_button">
            <CancelIcon />
          </CloseModal>
        ) : (
          <IconButton
            aria-label="close"
            icon="cancel"
            onClick={onClose}
            size="xs"
            variant="tertiary"
            margin={[0, 0, 0, 0]}
            data-testid="close_button"
          />
        )}
      </Sticky>

      {isLegacy ? (
        <Header>{headerText}</Header>
      ) : (
        <Headline variant={{ type: 'headline', order: '2', weight: 'normal' }} tag="h1">
          {headerText}
        </Headline>
      )}
    </>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const DigitalLeads: React.FC = () => {
  const dispatch = useDispatch();
  const digitalLeadsRef = useRef<HTMLDivElement>(null);

  // ========== SELECTORS ==========
  const { isModalOpen, leadId, error, errorMessage } = useSelector(selectLocalConfig);
  const contactForm = useSelector(selectForm);
  const { offers, dealerId, presetForm, handraiser } = useSelector(selectAppConfig);
  const { vehicle, vehicleByCarline, vehicleByAudiCode } = useSelector(selectDealerAndVehicleData);
  const { modelLineSelectCustom, modelLinesSelected } = useSelector(selectInputs);
  const { finalFormTitle } = useGetFormTitle();

  // Set analytics vehicle context for modelSelect forms
  setModelLineSelectCustom(
    presetForm === 'modelSelect' && modelLineSelectCustom ? modelLineSelectCustom : undefined,
  );

  // Set analytics form context for multipleSelect forms
  setModelLinesSelected(
    presetForm === 'multipleSelect' && modelLinesSelected?.length > 0
      ? modelLinesSelected
      : undefined,
  );

  // Set analytics context for handraiser forms
  setHandraiserConfig(handraiser ? { handraiser, formTitle: finalFormTitle || '' } : undefined);

  // Cleanup analytics context on unmount
  useEffect(() => {
    return () => {
      setModelLineSelectCustom(undefined);
      setModelLinesSelected(undefined);
      setHandraiserConfig(undefined);
    };
  }, []);

  // ========== CUSTOM HOOKS ==========
  const { dealer, isLoadingDealer } = useDealerInfo();
  const { isVehicleLoading } = useVehicleInfo();
  const { buildedForm } = useLeadsBuilder();

  const { specificSource: leadSource } = useSourceOfLead();

  const shouldKeepInputs =
    leadSource === 'vlp' || leadSource === 'vdp' || leadSource === 'offers' || leadSource === 'fym';
  const isVDPPage = leadSource === 'vdp';

  // Clear inputs when opening an event form to avoid confusion with previous data
  useEffect(() => {
    if (presetForm?.includes('event')) {
      dispatch(cleanInputs());
    }
  }, [presetForm, dispatch, cleanInputs]);

  // ========== VEHICLE DATA ==========
  const vehicleInView = vehicle || vehicleByCarline || vehicleByAudiCode;

  // ========== PROMOTION DATA ==========
  const promotion = React.useMemo(
    () => (offers ? convertToPromotionsType(offers) : undefined),
    [offers],
  );

  // ========== ANALYTICS TRACKING ==========
  const { vehicleWithEditedNames, vehicleDataForPromotions } = useIxisFormTracking({
    isModalOpen,
    vehicle,
    vehicleInView,
    offers,
    dealerId,
    leadSource,
    contactForm,
    promotion,
  });

  // Hook for generic form load
  useGenericFormLoad({ isModalOpen, presetForm, vehicle: undefined });

  // Hook for successful submission
  useSuccessfulFormSubmission({
    contactForm,
    leadId,
    vehicle: vehicleWithEditedNames ?? vehicleDataForPromotions,
    promotion,
  });

  // Hook for submission error
  useUnsuccessfulFormError({
    error,
    errorMessage,
    contactForm,
    vehicle: vehicleWithEditedNames ?? vehicleDataForPromotions,
    promotion,
  });

  // ========== HANDLERS ==========
  const handleCloseModal = () => {
    dispatch(setRemoveOffersAndFYM());
    dispatch(setClearVehicleData());
    dispatch(setModalIsClose());
    window.history.pushState('', document.title, window.location.pathname + window.location.search);
    if (!isVDPPage) dispatch(removeVehicleInfo());
    if (!shouldKeepInputs) dispatch(cleanInputs());
  };

  // Lock body scroll when modal is open
  useLockBodyScroll(isModalOpen);

  // ========== COMPUTED VALUES ==========
  const isGeneralForm = buildedForm.some(
    (toCheck) => toCheck.inputKey === INPUT_TYPE.TOPIC_OF_INTEREST,
  );
  const formName = getFormName(isGeneralForm, leadSource);

  // ========== DATA ATTRIBUTES ==========
  const isTier3 = !IS_T1_PAGE;
  const modalDataAttributes = getDataAttributes(vehicleInView as StockCarTypes, formName, isTier3);

  // ========== RENDER ==========
  const shouldBeFullWidth = !!(vehicleInView || isVehicleLoading || offers);

  const VehicleDetailsSection = useMemo(() => {
    if (!shouldBeFullWidth) return null;
    return (
      <VehicleDetailSection>
        <VehicleInfo
          dealer={dealer}
          vehicle={vehicleInView}
          isGeneralForm={isGeneralForm}
          isLoadingDealer={isLoadingDealer}
          isVehicleLoading={isVehicleLoading}
        />
      </VehicleDetailSection>
    );
  }, [dealer, isGeneralForm, isLoadingDealer, isVehicleLoading, shouldBeFullWidth, vehicleInView]);

  if (!isModalOpen) return null;

  return (
    <DigitalLeadsWrapper>
      <FocusTrap
        active={isModalOpen}
        focusTrapOptions={{ escapeDeactivates: false, clickOutsideDeactivates: true }}
      >
        {!leadId && !error ? (
          <DigitalLeadsModal
            ref={digitalLeadsRef}
            shouldBeFullWidth={shouldBeFullWidth}
            {...modalDataAttributes}
          >
            <ModalHeader
              dealerName={dealer?.name ?? undefined}
              formTitle={finalFormTitle}
              onClose={handleCloseModal}
            />
            <Content>
              <InputSection shouldBeFullWidth={shouldBeFullWidth}>
                {buildedForm.map((formInput) => (
                  <>{formInput.Component}</>
                ))}
                {presetForm !== 'schedService' && (
                  <>
                    <LegalDisclaimer />
                    <Submit />
                  </>
                )}
              </InputSection>
              {VehicleDetailsSection}
            </Content>
          </DigitalLeadsModal>
        ) : (
          <ResultModal handleCloseModal={handleCloseModal} isSuccess={!!leadId} />
        )}
      </FocusTrap>
      <BlackBackground onClick={handleCloseModal} data-testid="black_background" />
    </DigitalLeadsWrapper>
  );
};

export default DigitalLeads;
